/**
 * What to call a climb.
 *
 * Hardly anybody refers to a boulder by its name. On the wall it is "l'otto a
 * tacche sotto la grotta" or "il giallo a cerchi verdi nel diedro" — the grade,
 * what the holds look like, and where on the wall it is. So the name is optional
 * on everything but a board problem and a competition problem (see the model's
 * `name_required?`), and everywhere a climb has to be called something these two
 * functions answer for it.
 *
 * The server says the same thing in `display_name`, which is what a payload that
 * carries no grade or sector of its own falls back to.
 */

export interface DescribableClimb {
  name?: string | null;
  /** The server's own answer, for payloads that carry nothing else. */
  display_name?: string | null;
  difficulty?: string | null;
  hold_color?: string | null;
  zone?: { name: string } | null;
  type?: string;
}

/** What separates the three parts of a description, wherever one is built. */
const SEPARATOR = ' · ';

/**
 * How a climb is spoken about when nobody named it: its grade, what its holds
 * look like, and the sector it is in — as many of the three as it has. Empty
 * when it has none of them, which is a climb that only its name identifies.
 */
export function describeClimb(climb: DescribableClimb): string {
  return [climb.difficulty, climb.hold_color, climb.zone?.name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(SEPARATOR);
}

/**
 * The one label to use where there is room for exactly one — a breadcrumb, a
 * share sheet, the alt text of a photo. The name where there is one, because
 * that is what its setter chose to call it, and the description where there is
 * not.
 */
export function climbTitle(climb: DescribableClimb): string {
  return climb.name?.trim() || climb.display_name?.trim() || describeClimb(climb);
}

/**
 * How a climb is headed wherever there is room for two lines: what it is called
 * on the wall, and under it the name it was given — which most climbs no longer
 * have, and which nobody uses even when they do.
 *
 * A board problem is the exception and comes back headed by its name: it has
 * neither a colour nor a sector, so there is nothing else to call it, and its
 * name is exactly how it is looked up.
 */
export function climbHeading(climb: DescribableClimb): { heading: string; name: string | null } {
  const described = describeClimb(climb);
  const heading = climb.type === 'BoardClimb' || !described ? climbTitle(climb) : described;
  const name = climb.name?.trim();

  return { heading, name: name && name !== heading ? name : null };
}
